import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getAdminCode, setAdminCode } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Cake, LogOut, Plus, Pencil, Trash2, DollarSign, Calendar, Mail, Phone, Users, ClipboardList, Settings, ImageIcon, QrCode, CheckCircle2, Circle, Send, Copy, Filter, Truck, Store, Palette, ArrowUp, ArrowDown, Star } from "lucide-react";
import { toast } from "sonner";
import ImageUploader, { resolveImageUrl } from "@/components/ImageUploader";
import AvailabilityTab from "@/components/AvailabilityTab";
import ContentTab from "@/components/ContentTab";
import TagsInput from "@/components/TagsInput";
import FlavorPriceEditor from "@/components/FlavorPriceEditor";

const STATUS_COLORS = {
  new: "bg-amber-100 text-amber-700",
  confirmed: "bg-sky-100 text-blue-900",
  paid: "bg-emerald-100 text-emerald-700",
  completed: "bg-stone-100 text-stone-700",
  cancelled: "bg-red-100 text-red-700",
};

const CATEGORIES = ["cakes", "cupcakes", "cookies", "brownies", "cake_pops", "mini_cakes", "other"];

export default function AdminPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [menu, setMenu] = useState([]);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (!getAdminCode()) {
      navigate("/");
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    try {
      const [b, m, c] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/menu"),
        api.get("/config"),
      ]);
      setBookings(b.data);
      setMenu(m.data);
      setConfig(c.data);
      setReady(true);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAdminCode("");
        navigate("/");
      }
    }
  };

  const logout = () => { setAdminCode(""); navigate("/"); };

  if (!ready) return <div className="min-h-screen grid place-items-center text-stone-500">Loading…</div>;

  const paidCount = bookings.filter((b) => b.deposit_paid).length;
  const newCount = bookings.filter((b) => b.status === "new").length;

  return (
    <div data-testid={ADMIN.page} className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/cakesbyky-logo.png?v=2" alt="Cakesbyky" className="h-12 w-auto object-contain" data-testid="admin-header-logo" />
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400">Owner dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate("/admin/poster")}
              data-testid="admin-poster-btn"
              variant="ghost"
              className="text-stone-600 gap-2"
            >
              <QrCode className="h-4 w-4" /> QR Poster
            </Button>
            <Button data-testid={ADMIN.logout} onClick={logout} variant="ghost" className="text-stone-600 gap-2">
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          <Stat label="Total bookings" value={bookings.length} accent="teal" icon={ClipboardList} />
          <Stat label="New requests" value={newCount} accent="pink" icon={Calendar} />
          <Stat label="Paid deposits" value={paidCount} accent="teal" icon={DollarSign} />
          <Stat label="Menu items" value={menu.length} accent="pink" icon={Cake} />
        </div>

        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="bg-white border border-stone-200 rounded-full p-1 h-11">
            <TabsTrigger data-testid={ADMIN.tabBookings} value="bookings" className="rounded-full data-[state=active]:bg-blue-900 data-[state=active]:text-white px-5">Bookings</TabsTrigger>
            <TabsTrigger data-testid={ADMIN.tabMenu} value="menu" className="rounded-full data-[state=active]:bg-blue-900 data-[state=active]:text-white px-5">Menu</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-availability" value="availability" className="rounded-full data-[state=active]:bg-blue-900 data-[state=active]:text-white px-5">Availability</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-content" value="content" className="rounded-full data-[state=active]:bg-blue-900 data-[state=active]:text-white px-5">Content</TabsTrigger>
            <TabsTrigger data-testid={ADMIN.tabSettings} value="settings" className="rounded-full data-[state=active]:bg-blue-900 data-[state=active]:text-white px-5">Settings & Payments</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-6">
            <BookingsTab bookings={bookings} onReload={loadAll} config={config} />
          </TabsContent>
          <TabsContent value="menu" className="mt-6">
            <MenuTab menu={menu} onReload={loadAll} />
          </TabsContent>
          <TabsContent value="availability" className="mt-6">
            <AvailabilityTab bookings={bookings} />
          </TabsContent>
          <TabsContent value="content" className="mt-6">
            <ContentTab config={config} onReload={loadAll} />
          </TabsContent>
          <TabsContent value="settings" className="mt-6">
            <SettingsTab config={config} onReload={loadAll} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, icon: Icon }) {
  const bg = accent === "teal" ? "bg-sky-50 text-blue-800" : "bg-amber-50 text-amber-500";
  return (
    <Card className="border-stone-100">
      <CardContent className="p-5 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-stone-400">{label}</p>
          <p className="font-serif-display text-3xl mt-1">{value}</p>
        </div>
        <div className={`h-11 w-11 rounded-full grid place-items-center ${bg}`}>
          <Icon className="h-5 w-5" />
        </div>
      </CardContent>
    </Card>
  );
}

function BookingsTab({ bookings, onReload, config }) {
  const cashTag = (config?.cashapp_tag || "").replace(/^\$/, "");
  const cashappNote = config?.cashapp_note || "";
  const businessName = config?.business_name || "us";
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState(""); // YYYY-MM-DD, "" = any
  const [selected, setSelected] = useState(new Set());

  const STATUSES = [
    { key: "all", label: "All" },
    { key: "new", label: "New" },
    { key: "confirmed", label: "Confirmed" },
    { key: "completed", label: "Completed" },
    { key: "cancelled", label: "Cancelled" },
  ];

  const uniqueEventDates = [...new Set((bookings || []).map((b) => b.event_date))].sort();

  const filtered = (bookings || []).filter((b) => {
    if (statusFilter !== "all" && b.status !== statusFilter) return false;
    if (dateFilter && b.event_date !== dateFilter) return false;
    return true;
  });

  const toggleSelected = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const selectAllVisible = () => setSelected(new Set(filtered.filter((b) => b.status === "new").map((b) => b.id)));

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success("Status updated");
      onReload();
    } catch { toast.error("Update failed"); }
  };
  const toggleDeposit = async (id, current) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { deposit_paid: !current });
      toast.success(current ? "Marked as unpaid" : "Deposit marked received");
      onReload();
    } catch { toast.error("Update failed"); }
  };

  const bookingTotal = (b) => {
    const treats = (b.items || []).reduce(
      (sum, it) => sum + (it.flavors || []).reduce((a, f) => a + (f.quantity || 0) * (f.price_per_dozen || 0), 0),
      0
    );
    return treats + (b.design_fee || 0) + (b.delivery_fee || 0);
  };

  const buildPaymentMessage = (b) => {
    const total = bookingTotal(b);
    const link = cashTag ? `https://cash.app/$${cashTag}${total > 0 ? `/${total.toFixed(2)}` : ""}` : "";
    const fulfillmentLine = b.fulfillment === "delivery"
      ? `Delivery to: ${b.delivery_address || "(address on file)"} (+$${(b.delivery_fee || 0).toFixed(2)})`
      : b.fulfillment === "pickup" ? "Pickup (free)" : null;
    const designLine = b.includes_design ? `Includes custom design (+$${(b.design_fee || 0).toFixed(2)})` : null;
    const lines = [
      `Hi ${b.name}! Your ${b.event_type} order with ${businessName} on ${b.event_date} has been approved. 🎉`,
      fulfillmentLine,
      designLine,
      total > 0 ? `Estimated total: $${total.toFixed(2)}.` : null,
      link ? `To reserve your date, please send your deposit via Cash App: ${link}` : "We'll send Cash App details separately.",
      cashappNote ? cashappNote : (link ? "Include your name and event date in the Cash App note." : null),
    ].filter(Boolean);
    return { message: lines.join("\n\n"), link };
  };

  const approveAndCopy = async (b) => {
    const { message, link } = buildPaymentMessage(b);
    // Open Cash App tab synchronously to survive popup blockers
    const cashWindow = link ? window.open(link, "_blank", "noopener,noreferrer") : null;
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Message copied — paste it into a text or email to the customer.");
    } catch {
      toast.info("Message ready (clipboard not available).");
    }
    try {
      await api.patch(`/admin/bookings/${b.id}`, { status: "confirmed" });
      onReload();
    } catch {
      toast.error("Status update failed but message copied.");
      if (cashWindow) cashWindow.close();
    }
  };

  const copyLinkOnly = async (b) => {
    const { link } = buildPaymentMessage(b);
    if (!link) { toast.error("Set your $cashtag in Settings first."); return; }
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Cash App link copied");
    } catch { toast.error("Copy failed"); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Deleted");
      onReload();
    } catch { toast.error("Delete failed"); }
  };

  const bulkApprove = async () => {
    const targets = filtered.filter((b) => selected.has(b.id) && b.status === "new");
    if (targets.length === 0) { toast.error("No new bookings selected"); return; }
    const combined = targets.map((b) => buildPaymentMessage(b).message).join("\n\n---\n\n");
    try { await navigator.clipboard.writeText(combined); } catch (clipErr) { console.warn("clipboard write failed", clipErr); }
    // Parallel PATCH each booking so large batches feel snappy
    const results = await Promise.allSettled(
      targets.map((b) => api.patch(`/admin/bookings/${b.id}`, { status: "confirmed" }))
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    if (failed === 0) toast.success(`${ok} approved · messages copied`);
    else toast.warning(`${ok} approved, ${failed} failed · messages copied`);
    clearSelection();
    onReload();
  };

  if (!bookings.length) return <EmptyState icon={ClipboardList} label="No bookings yet" hint="New requests will appear here." />;

  const selectableCount = filtered.filter((b) => b.status === "new").length;
  const selectedCount = filtered.filter((b) => selected.has(b.id) && b.status === "new").length;

  return (
    <div>
      <div className="mb-4 bg-white rounded-2xl border border-stone-100 p-3 flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-stone-400 ml-1" />
        {STATUSES.map((s) => {
          const count = s.key === "all" ? bookings.length : bookings.filter((b) => b.status === s.key).length;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => { setStatusFilter(s.key); clearSelection(); }}
              data-testid={`admin-filter-${s.key}`}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${statusFilter === s.key ? "bg-blue-900 text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-blue-300"}`}
            >
              {s.label} <span className={`ml-1 ${statusFilter === s.key ? "text-sky-100" : "text-stone-400"}`}>({count})</span>
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2">
          <select
            value={dateFilter}
            onChange={(e) => { setDateFilter(e.target.value); clearSelection(); }}
            data-testid="admin-filter-date"
            className="text-xs h-8 rounded-full border border-stone-200 px-3 text-stone-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            <option value="">All event dates</option>
            {uniqueEventDates.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
      </div>

      {selectableCount > 0 && (
        <div className="mb-3 flex items-center justify-between text-xs text-stone-500 px-2">
          <button
            type="button"
            onClick={selectedCount === selectableCount ? clearSelection : selectAllVisible}
            data-testid="admin-select-all"
            className="text-blue-800 hover:underline"
          >
            {selectedCount === selectableCount ? "Clear selection" : `Select all ${selectableCount} new`}
          </button>
          {selectedCount > 0 && <span data-testid="admin-selection-count">{selectedCount} selected</span>}
        </div>
      )}

      {selectedCount > 0 && (
        <div className="sticky top-2 z-10 mb-4 bg-blue-900 text-white rounded-2xl shadow-lg p-3 flex items-center gap-3">
          <span className="text-sm flex-1">{selectedCount} new booking{selectedCount === 1 ? "" : "s"} selected</span>
          <Button
            data-testid="admin-bulk-approve"
            onClick={bulkApprove}
            className="rounded-full bg-white text-blue-900 hover:bg-sky-50 gap-2 border-0"
            size="sm"
          >
            <Send className="h-4 w-4" /> Approve {selectedCount} & copy messages
          </Button>
          <button
            onClick={clearSelection}
            className="text-white/80 hover:text-white text-sm"
            data-testid="admin-clear-selection"
          >
            Cancel
          </button>
        </div>
      )}

      <div className="grid gap-4">
        {filtered.length === 0 ? (
          <EmptyState icon={ClipboardList} label="No bookings match" hint="Try a different filter." />
        ) : filtered.map((b) => (
          <BookingCard
            key={b.id}
            b={b}
            selected={selected.has(b.id)}
            onToggleSelect={() => toggleSelected(b.id)}
            onUpdateStatus={updateStatus}
            onToggleDeposit={toggleDeposit}
            onApprove={approveAndCopy}
            onCopyLink={copyLinkOnly}
            onDelete={del}
            cashTag={cashTag}
          />
        ))}
      </div>
    </div>
  );
}

function BookingCard({ b, selected, onToggleSelect, onUpdateStatus, onToggleDeposit, onApprove, onCopyLink, onDelete, cashTag }) {
  const isNew = b.status === "new";
  return (
    <Card data-testid={ADMIN.bookingRow} className={`border-stone-100 ${selected ? "ring-2 ring-blue-500" : ""}`}>
      <CardContent className="p-6">
        <div className="flex flex-wrap justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            {isNew && (
              <input
                type="checkbox"
                checked={selected}
                onChange={onToggleSelect}
                data-testid={`admin-select-${b.id}`}
                className="mt-1.5 h-4 w-4 accent-blue-900"
                aria-label="Select booking"
              />
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-serif-display text-2xl">{b.name}</h3>
                <Badge className={STATUS_COLORS[b.status] || "bg-stone-100"}>{b.status}</Badge>
                {b.deposit_paid && <Badge className="bg-emerald-100 text-emerald-700">Deposit Paid</Badge>}
                {b.fulfillment === "delivery" && (
                  <Badge className="bg-sky-100 text-blue-900 flex items-center gap-1" data-testid={`admin-badge-delivery-${b.id}`}>
                    <Truck className="h-3 w-3" /> Delivery
                  </Badge>
                )}
                {b.fulfillment === "pickup" && (
                  <Badge variant="outline" className="border-stone-200 text-stone-600 flex items-center gap-1" data-testid={`admin-badge-pickup-${b.id}`}>
                    <Store className="h-3 w-3" /> Pickup
                  </Badge>
                )}
                {b.includes_design && (
                  <Badge className="bg-amber-100 text-amber-700 flex items-center gap-1" data-testid={`admin-badge-design-${b.id}`}>
                    <Palette className="h-3 w-3" /> Design
                  </Badge>
                )}
              </div>
              <p className="text-sm text-stone-500 mt-1">{b.event_type} · {b.event_date}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Select value={b.status} onValueChange={(v) => onUpdateStatus(b.id, v)}>
              <SelectTrigger className="w-40 rounded-full h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["new", "confirmed", "paid", "completed", "cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" className="text-stone-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Delete booking?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => onDelete(b.id)} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 text-sm text-stone-600">
          <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-stone-400" />{b.email}</div>
          <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-stone-400" />{b.phone}</div>
          <div className="flex items-center gap-2"><Users className="h-4 w-4 text-stone-400" />{b.guest_count} guests</div>
        </div>
        {b.items && b.items.length > 0 ? (
          <div className="mt-4 space-y-2">
            {b.items.map((it, idx) => {
              const itemTotal = (it.flavors || []).reduce((a, f) => a + (f.quantity || 0) * (f.price_per_dozen || 0), 0);
              return (
                <div key={`${it.menu_item_id || it.menu_item_name}-${idx}`} className="rounded-xl border border-stone-100 bg-stone-50/40 p-3">
                  <div className="flex justify-between items-baseline mb-1">
                    <p className="text-sm font-medium text-stone-800">{it.menu_item_name}</p>
                    {itemTotal > 0 && <p className="text-sm text-blue-900 font-medium">${itemTotal.toFixed(2)}</p>}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(it.flavors || []).map((f, i) => (
                      <Badge key={`${f.flavor}-${i}`} variant="outline" className="rounded-full bg-white">
                        {f.flavor} × <span className="font-semibold ml-0.5">{f.quantity} dz</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              );
            })}
            {(() => {
              const treats = (b.items || []).reduce((sum, it) => sum + (it.flavors || []).reduce((a, f) => a + (f.quantity || 0) * (f.price_per_dozen || 0), 0), 0);
              const design = b.design_fee || 0;
              const delivery = b.delivery_fee || 0;
              const total = treats + design + delivery;
              if (total <= 0 && !b.includes_design && !b.fulfillment) return null;
              return (
                <div className="pt-2 border-t border-stone-100 space-y-1 text-sm">
                  {(design > 0 || delivery > 0 || b.fulfillment) && (
                    <>
                      {design > 0 && (
                        <div className="flex justify-between text-stone-600" data-testid={`admin-booking-design-${b.id}`}>
                          <span className="flex items-center gap-1.5"><Palette className="h-3.5 w-3.5 text-amber-500" />Custom design</span>
                          <span>${design.toFixed(2)}</span>
                        </div>
                      )}
                      {b.design_reference_url && (
                        <a
                          href={resolveImageUrl(b.design_reference_url)}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`admin-booking-design-ref-${b.id}`}
                          className="flex items-center gap-3 rounded-xl bg-white border border-amber-100 p-2 hover:border-amber-300 transition-colors"
                        >
                          <img src={resolveImageUrl(b.design_reference_url)} alt="Reference" className="h-14 w-14 rounded-lg object-cover" />
                          <div className="text-xs text-stone-500">
                            <div className="font-medium text-stone-700">Design reference</div>
                            <div>Click to open full-size</div>
                          </div>
                        </a>
                      )}
                      {b.fulfillment === "delivery" ? (
                        <div className="flex justify-between text-stone-600" data-testid={`admin-booking-delivery-${b.id}`}>
                          <span className="flex items-center gap-1.5"><Truck className="h-3.5 w-3.5 text-blue-900" />Delivery{b.delivery_address ? ` — ${b.delivery_address}` : ""}{b.delivery_distance_miles ? ` (${b.delivery_distance_miles.toFixed(1)} mi)` : ""}</span>
                          <span>${delivery.toFixed(2)}</span>
                        </div>
                      ) : b.fulfillment === "pickup" && (
                        <div className="flex justify-between text-stone-600" data-testid={`admin-booking-pickup-${b.id}`}>
                          <span className="flex items-center gap-1.5"><Store className="h-3.5 w-3.5 text-blue-900" />Pickup</span>
                          <span className="text-stone-400">Free</span>
                        </div>
                      )}
                    </>
                  )}
                  {total > 0 && (
                    <div className="flex justify-between items-baseline pt-2 border-t border-stone-100">
                      <span className="text-stone-500">Est. total</span>
                      <span className="font-serif-display text-lg text-blue-900" data-testid={`admin-booking-total-${b.id}`}>${total.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        ) : (
          b.treats_requested?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {b.treats_requested.map((t) => <Badge key={t} variant="outline" className="rounded-full">{t.replace("_", " ")}</Badge>)}
            </div>
          )
        )}
        {b.flavors && <p className="text-sm text-stone-600 mt-3"><span className="text-stone-400">Flavors:</span> {b.flavors}</p>}
        {b.notes && <p className="text-sm text-stone-600 mt-1"><span className="text-stone-400">Notes:</span> {b.notes}</p>}
        <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs text-stone-400">Booked {new Date(b.created_at).toLocaleString()}</div>
          <div className="flex flex-wrap gap-2">
            {isNew && (
              <Button
                data-testid="admin-approve-btn"
                onClick={() => onApprove(b)}
                size="sm"
                className="rounded-full gap-2 bg-blue-900 hover:bg-blue-800 text-white border-0"
              >
                <Send className="h-4 w-4" />
                Approve & send payment link
              </Button>
            )}
            {cashTag && (
              <Button
                data-testid="admin-copy-link-btn"
                onClick={() => onCopyLink(b)}
                variant="outline"
                size="sm"
                className="rounded-full gap-2 border-stone-200 text-stone-600 hover:border-blue-300"
              >
                <Copy className="h-4 w-4" />
                Copy link
              </Button>
            )}
            <Button
              data-testid="admin-toggle-deposit"
              onClick={() => onToggleDeposit(b.id, b.deposit_paid)}
              variant="outline"
              size="sm"
              className={`rounded-full gap-2 border ${b.deposit_paid ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100" : "border-stone-200 text-stone-600 hover:border-blue-300"}`}
            >
              {b.deposit_paid ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
              {b.deposit_paid ? "Deposit received" : "Mark deposit received"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MenuTab({ menu, onReload }) {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const openNew = () => { setEditing({ name: "", category: "cakes", description: "", price_from: 0, image_url: "", images: [], flavors: [], active: true }); setOpen(true); };
  const openEdit = (item) => { setEditing({ ...item, flavors: item.flavors || [], images: item.images && item.images.length ? item.images : (item.image_url ? [item.image_url] : []) }); setOpen(true); };

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/menu/${editing.id}`, editing);
      else await api.post("/admin/menu", editing);
      toast.success("Saved");
      setOpen(false); onReload();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/menu/${id}`); toast.success("Deleted"); onReload(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button data-testid={ADMIN.addMenuBtn} onClick={openNew} className="btn-pill btn-navy border-0 gap-2"><Plus className="h-4 w-4" />Add item</Button>
      </div>
      {menu.length === 0 && <EmptyState icon={Cake} label="No menu items" hint="Add treats to show on the public menu." />}
      <div className="grid gap-3">
        {menu.map((m) => (
          <Card data-testid={ADMIN.menuItemRow} key={m.id} className="border-stone-100">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="h-16 w-16 rounded-xl overflow-hidden shrink-0 bg-amber-50 grid place-items-center">
                {m.image_url ? <img src={resolveImageUrl(m.image_url)} alt={m.name} className="h-full w-full object-cover" /> : <ImageIcon className="h-6 w-6 text-amber-300" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-stone-900">{m.name}</p>
                  {!m.active && <Badge variant="outline">Hidden</Badge>}
                </div>
                <p className="text-xs text-stone-500 capitalize">{m.category.replace("_", " ")} · from ${m.price_from?.toFixed(2)}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => openEdit(m)} className="text-stone-500"><Pencil className="h-4 w-4" /></Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-stone-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>{`Delete "${m.name}"?`}</AlertDialogTitle></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(m.id)} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">{editing?.id ? "Edit item" : "New menu item"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <FieldRow label="Name"><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></FieldRow>
              <FieldRow label="Category">
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </FieldRow>
              <FieldRow label="Description"><Textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></FieldRow>
              <div className="grid grid-cols-2 gap-3">
                <FieldRow label="Starting price ($)"><Input type="number" value={editing.price_from} onChange={(e) => setEditing({ ...editing, price_from: parseFloat(e.target.value) || 0 })} /></FieldRow>
                <FieldRow label="Active">
                  <Select value={String(editing.active)} onValueChange={(v) => setEditing({ ...editing, active: v === "true" })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="true">Visible</SelectItem><SelectItem value="false">Hidden</SelectItem></SelectContent>
                  </Select>
                </FieldRow>
              </div>
              <FieldRow label="Flavors & pricing (per dozen)">
                <FlavorPriceEditor
                  value={editing.flavors || []}
                  onChange={(flavors) => setEditing({ ...editing, flavors })}
                  testId="menu-flavor"
                />
                <p className="text-xs text-stone-400 mt-1">Set a price per dozen for each flavor — customers see it live on the booking form.</p>
              </FieldRow>
              <FieldRow label="Photos (first is the cover)">
                <MenuImagesEditor
                  images={editing.images || []}
                  onChange={(images) => setEditing({ ...editing, images, image_url: images[0] || "" })}
                />
              </FieldRow>
              <details className="text-xs">
                <summary className="cursor-pointer text-stone-500 hover:text-stone-700">Or paste an external image URL to add</summary>
                <ExternalUrlAdder onAdd={(url) => {
                  const next = [...(editing.images || []), url];
                  setEditing({ ...editing, images: next, image_url: next[0] });
                }} />
              </details>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-blue-900 hover:bg-blue-800">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SettingsTab({ config, onReload }) {
  const [f, setF] = useState(config);
  const [saving, setSaving] = useState(false);
  const setK = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/config", f);
      toast.success("Settings updated");
      onReload();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="border-stone-100">
        <CardHeader><CardTitle className="font-serif-display text-2xl flex items-center gap-2"><Settings className="h-5 w-5 text-blue-900" />Business Info</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <FieldRow label="Business name"><Input value={f.business_name} onChange={(e) => setK("business_name", e.target.value)} /></FieldRow>
          <FieldRow label="Tagline"><Input value={f.tagline} onChange={(e) => setK("tagline", e.target.value)} /></FieldRow>
          <FieldRow label="About"><Textarea rows={3} value={f.about} onChange={(e) => setK("about", e.target.value)} /></FieldRow>
          <FieldRow label="Email"><Input value={f.email} onChange={(e) => setK("email", e.target.value)} /></FieldRow>
          <FieldRow label="Phone"><Input value={f.phone || ""} onChange={(e) => setK("phone", e.target.value)} /></FieldRow>
          <FieldRow label="Instagram handle"><Input value={f.instagram || ""} onChange={(e) => setK("instagram", e.target.value)} /></FieldRow>
          <FieldRow label="Hero image">
            <ImageUploader
              value={f.hero_image_url}
              onChange={(url) => setK("hero_image_url", url)}
              testId="hero-image-uploader"
            />
          </FieldRow>
          <FieldRow label="Public site URL (for QR codes & marketing)">
            <Input placeholder="https://cakesbyky.com" value={f.public_site_url || ""} onChange={(e) => setK("public_site_url", e.target.value)} data-testid="admin-public-site-url" />
          </FieldRow>
        </CardContent>
      </Card>

      <Card className="border-stone-100">
        <CardHeader><CardTitle className="font-serif-display text-2xl flex items-center gap-2"><DollarSign className="h-5 w-5 text-amber-500" />Payments</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <FieldRow label="Booking deposit amount (USD)"><Input type="number" value={f.deposit_amount} onChange={(e) => setK("deposit_amount", parseFloat(e.target.value) || 0)} /></FieldRow>

          <div className="rounded-2xl bg-[#00D632]/10 border border-[#00D632]/30 p-4 text-sm text-stone-700 space-y-3">
            <p className="font-medium text-[#008A24] flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Cash App
            </p>
            <FieldRow label="Your $cashtag">
              <div className="flex items-center gap-2">
                <span className="text-stone-500 font-mono">$</span>
                <Input
                  data-testid="admin-cashapp-tag"
                  placeholder="cakesbyky"
                  value={(f.cashapp_tag || "").replace(/^\$/, "")}
                  onChange={(e) => setK("cashapp_tag", e.target.value.replace(/^\$/, "").trim())}
                />
              </div>
            </FieldRow>
            <FieldRow label="Instruction shown to the customer">
              <Textarea
                rows={2}
                data-testid="admin-cashapp-note"
                placeholder="Please include your name & event date in the Cash App note."
                value={f.cashapp_note || ""}
                onChange={(e) => setK("cashapp_note", e.target.value)}
              />
            </FieldRow>
            {f.cashapp_tag && (
              <div className="rounded-xl bg-white/70 border border-[#00D632]/20 px-3 py-2">
                <p className="text-xs text-stone-500 mb-1">Preview link</p>
                <a
                  href={`https://cash.app/$${f.cashapp_tag.replace(/^\$/, "")}/${f.deposit_amount || 0}`}
                  target="_blank" rel="noreferrer"
                  className="text-sm text-[#008A24] font-mono break-all hover:underline"
                >
                  https://cash.app/${f.cashapp_tag.replace(/^\$/, "")}/{f.deposit_amount || 0}
                </a>
              </div>
            )}
          </div>

          <Button data-testid="admin-save-config-btn" onClick={save} disabled={saving} className="w-full bg-blue-900 hover:bg-blue-800 rounded-full h-11">
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-stone-100 md:col-span-2">
        <CardHeader><CardTitle className="font-serif-display text-2xl flex items-center gap-2"><Truck className="h-5 w-5 text-blue-900" />Add-ons & Delivery</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <FieldRow label="Custom design fee (USD)">
            <Input
              type="number" min="0" step="1"
              data-testid="admin-design-fee"
              value={f.design_fee ?? 20}
              onChange={(e) => setK("design_fee", parseFloat(e.target.value) || 0)}
            />
          </FieldRow>
          <FieldRow label="Delivery fee (USD)">
            <Input
              type="number" min="0" step="1"
              data-testid="admin-delivery-fee"
              value={f.delivery_fee ?? 30}
              onChange={(e) => setK("delivery_fee", parseFloat(e.target.value) || 0)}
            />
          </FieldRow>
          <FieldRow label="Delivery origin address">
            <Input
              data-testid="admin-delivery-origin"
              placeholder="123 Bakery Lane, City, ST 12345"
              value={f.delivery_origin_address || ""}
              onChange={(e) => setK("delivery_origin_address", e.target.value)}
            />
          </FieldRow>
          <FieldRow label="Max delivery radius (miles)">
            <Input
              type="number" min="0" step="1"
              data-testid="admin-delivery-max-miles"
              value={f.delivery_max_miles ?? 15}
              onChange={(e) => setK("delivery_max_miles", parseFloat(e.target.value) || 0)}
            />
          </FieldRow>
          <p className="text-xs text-stone-500 sm:col-span-2">Customers who pick delivery must enter an address; if it's farther than your radius from the origin, we'll block the booking automatically.</p>
        </CardContent>
      </Card>

      <Card className="border-stone-100 md:col-span-2">
        <CardHeader><CardTitle className="font-serif-display text-2xl flex items-center gap-2"><Palette className="h-5 w-5 text-amber-500" />Design Reference Gallery</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-stone-500 mb-4">Photos of past designs customers can pick as inspiration when they add the custom design fee.</p>
          <DesignGalleryEditor />
        </CardContent>
      </Card>
    </div>
  );
}

function DesignGalleryEditor() {
  const [images, setImages] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/design-gallery");
      setImages(data?.images || []);
    } catch (e) { console.error("design-gallery load failed", e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const addUrl = async (url) => {
    if (!url) return;
    try {
      const { data } = await api.post("/admin/design-gallery", { url });
      setImages(data?.images || []);
      toast.success("Added to gallery");
    } catch { toast.error("Add failed"); }
  };

  const removeUrl = async (url) => {
    try {
      const { data } = await api.delete(`/admin/design-gallery`, { params: { url } });
      setImages(data?.images || []);
      toast.success("Removed");
    } catch { toast.error("Remove failed"); }
  };

  return (
    <div className="space-y-3" data-testid="admin-design-gallery">
      <div className="rounded-xl border border-dashed border-stone-200 p-3">
        <ImageUploader
          value=""
          onChange={(url) => { if (url) addUrl(url); }}
          testId="admin-gallery-uploader"
        />
      </div>
      {images.length === 0 ? (
        <p className="text-xs text-stone-400 italic">No design references yet. Upload a photo above.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {images.map((url) => (
            <div key={url} className="relative group rounded-xl overflow-hidden border border-stone-200 aspect-square" data-testid={`admin-gallery-item`}>
              <img src={resolveImageUrl(url)} alt="Design reference" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => removeUrl(url)}
                className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/60 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function MenuImagesEditor({ images, onChange }) {
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const remove = (idx) => onChange(images.filter((_, i) => i !== idx));
  const makeCover = (idx) => {
    if (idx === 0) return;
    const next = [images[idx], ...images.filter((_, i) => i !== idx)];
    onChange(next);
  };
  const add = (url) => { if (url) onChange([...images, url]); };

  return (
    <div className="space-y-3" data-testid="menu-images-editor">
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {images.map((url, idx) => (
            <div key={`${url}-${idx}`} className="relative group aspect-square rounded-xl overflow-hidden border border-stone-200" data-testid="menu-image-tile">
              <img src={resolveImageUrl(url)} alt={`Photo ${idx + 1}`} className="h-full w-full object-cover" />
              {idx === 0 && (
                <span className="absolute top-1.5 left-1.5 rounded-full bg-blue-900 text-white text-[10px] font-semibold px-2 py-0.5 flex items-center gap-1 shadow" data-testid="menu-image-cover-badge">
                  <Star className="h-3 w-3" /> Cover
                </span>
              )}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                {idx > 0 && (
                  <button type="button" onClick={() => makeCover(idx)} title="Make cover" className="h-8 w-8 rounded-full bg-white/90 text-stone-700 grid place-items-center hover:bg-white" data-testid={`menu-image-set-cover-${idx}`}>
                    <Star className="h-4 w-4" />
                  </button>
                )}
                <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} title="Move left" className="h-8 w-8 rounded-full bg-white/90 text-stone-700 grid place-items-center hover:bg-white disabled:opacity-30" data-testid={`menu-image-up-${idx}`}>
                  <ArrowUp className="h-4 w-4 -rotate-90" />
                </button>
                <button type="button" onClick={() => move(idx, 1)} disabled={idx === images.length - 1} title="Move right" className="h-8 w-8 rounded-full bg-white/90 text-stone-700 grid place-items-center hover:bg-white disabled:opacity-30" data-testid={`menu-image-down-${idx}`}>
                  <ArrowDown className="h-4 w-4 -rotate-90" />
                </button>
                <button type="button" onClick={() => remove(idx)} title="Remove" className="h-8 w-8 rounded-full bg-red-500 text-white grid place-items-center hover:bg-red-600" data-testid={`menu-image-remove-${idx}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <ImageUploader
        value=""
        onChange={(url) => add(url)}
        testId="menu-image-uploader"
      />
      {images.length === 0 && <p className="text-xs text-stone-400 italic">No photos yet. Upload one — it becomes the cover automatically.</p>}
    </div>
  );
}

function ExternalUrlAdder({ onAdd }) {
  const [url, setUrl] = useState("");
  return (
    <div className="mt-2 flex gap-2">
      <Input placeholder="https://..." value={url} onChange={(e) => setUrl(e.target.value)} />
      <Button
        type="button"
        onClick={() => { if (url.trim()) { onAdd(url.trim()); setUrl(""); } }}
        className="bg-blue-900 hover:bg-blue-800 shrink-0"
        data-testid="menu-image-add-url"
      >
        Add
      </Button>
    </div>
  );
}

function EmptyState({ icon: Icon, label, hint }) {
  return (
    <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-stone-200">
      <Icon className="h-10 w-10 mx-auto text-stone-300 mb-3" />
      <p className="font-serif-display text-2xl text-stone-700">{label}</p>
      <p className="text-sm text-stone-500 mt-1">{hint}</p>
    </div>
  );
}
